// Lumynest "Complete the set": adds the dress and the heels to the bag in one request and
// lets the theme's cart drawer open/refresh exactly like a normal add to cart.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class LumynestSet extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-lum-set-add]');
    this.error = this.querySelector('[data-lum-set-error]');
    this.handleClick = () => this.addSet();
    this.handleChange = () => this.clearError();
    this.button?.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
  }

  clearError() {
    if (this.error) {
      this.error.hidden = true;
      this.error.textContent = '';
    }
  }

  /** @param {string} message */
  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  /**
   * Resolves the chosen variant for one item, or returns an error message.
   * @param {HTMLElement} item
   * @returns {{id?: number, message?: string}}
   */
  resolveItem(item) {
    let variants = [];
    try {
      variants = JSON.parse(item.querySelector('[data-lum-variants]')?.textContent || '[]');
    } catch (e) {
      variants = [];
    }
    const selects = /** @type {HTMLSelectElement[]} */ ([...item.querySelectorAll('select[data-opt-index]')]);
    for (const select of selects) {
      if (!select.value) return { message: `Please choose a ${(select.dataset.optLabel || 'option').toLowerCase()} for ${item.dataset.title || 'each piece'}.` };
    }
    const chosen = selects.map((s) => s.value);
    const match = variants.find((v) => chosen.every((value, i) => v.options[i] === value));
    if (!match) return { message: `That combination isn't available for ${item.dataset.title || 'this piece'}.` };
    if (!match.available) return { message: `${chosen.join(' / ')} is sold out for ${item.dataset.title || 'this piece'}. Please choose another option.` };
    return { id: match.id };
  }

  addSet() {
    this.clearError();
    const items = [];
    for (const item of /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-lum-set-item]')])) {
      const result = this.resolveItem(item);
      if (!result.id) {
        this.showError(result.message || 'Please choose your options.');
        return;
      }
      items.push({ id: result.id, quantity: 1 });
    }
    if (!items.length || !this.button) return;

    this.button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const theme = /** @type {any} */ (window).Theme;
    const addUrl = theme?.routes?.cart_add_url || '/cart/add.js';
    const cartUrl = theme?.routes?.cart_url || '/cart';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const cart = await fetch(`${cartUrl}.json`).then((r) => r.json());
        if (response.status) {
          this.showError(response.description || response.message || 'Sorry, we could not add the set. Please try again.');
          this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'lumynest-set', itemCount: 0 },
          });
          return;
        }
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'lumynest-set',
            itemCount: items.length,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error('[lumynest-set]', error);
        deferred.reject(error);
        this.showError('Sorry, we could not add the set. Please try again.');
      })
      .finally(() => {
        this.button?.removeAttribute('aria-busy');
      });
  }
}

if (!customElements.get('lumynest-set')) customElements.define('lumynest-set', LumynestSet);
